import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PasswordInput from '../../components/common/PasswordInput';
import './VerifyEmail.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface TokenInfo {
  valid: boolean;
  email_verified: boolean;
  password_set: boolean;
  email: string;
  name: string;
  expired: boolean;
  error?: string;
}

interface VerifyEmailProps {
  accountType: 'portal' | 'bridge-club' | 'staff';
}

const VerifyEmail: React.FC<VerifyEmailProps> = ({ accountType }) => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Determine API paths based on account type
  const apiPaths = {
    'portal': {
      verify: '/portal/verify-email/',
      setPassword: '/portal/set-password/',
      checkToken: '/verify-token/portal/',
      redirect: '/portal/login',
      portalName: 'Customer Portal',
    },
    'bridge-club': {
      verify: '/bridge-club/verify-email/',
      setPassword: '/bridge-club/set-password/',
      checkToken: '/verify-token/bridge-club/',
      redirect: '/bridge-club/login',
      portalName: 'Bridge Club',
    },
    'staff': {
      verify: '/auth/verify-email/',
      setPassword: '/auth/set-password/',
      checkToken: '/verify-token/staff/',
      redirect: '/login',
      portalName: 'Admin Portal',
    },
  };

  const paths = apiPaths[accountType];

  // Check token validity on mount
  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setError('No verification token provided');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}${paths.checkToken}${token}/`);
        setTokenInfo(response.data);

        if (response.data.expired) {
          setError('This verification link has expired. Please request a new one.');
        } else if (response.data.email_verified && response.data.password_set) {
          // Already complete - redirect to login
          setSuccess(true);
          setTimeout(() => navigate(paths.redirect), 2000);
        } else if (!response.data.email_verified) {
          // Need to verify email first
          await verifyEmail();
        }
        // If verified but password not set, show password form
      } catch (err: any) {
        console.error('Token check error:', err);
        if (err.response?.status === 404) {
          setError('Invalid verification link. It may have already been used or expired.');
        } else {
          setError('Failed to verify link. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    checkToken();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}${paths.verify}`, { token });
      setTokenInfo(prev => prev ? { ...prev, email_verified: true } : null);
      return true;
    } catch (err: any) {
      console.error('Verify error:', err);
      setError(err.response?.data?.error || 'Failed to verify email');
      return false;
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}${paths.setPassword}`, {
        token,
        password,
      }, { withCredentials: true });

      setSuccess(true);

      // For Bridge Club, user may be auto-logged in via cookies
      if (accountType === 'bridge-club' && response.data.member) {
        setTimeout(() => navigate('/bridge-club/dashboard'), 1500);
      } else if (accountType === 'portal') {
        // Portal accounts need admin approval
        setTimeout(() => navigate('/portal/login'), 2000);
      } else {
        setTimeout(() => navigate(paths.redirect), 2000);
      }
    } catch (err: any) {
      console.error('Set password error:', err);
      setError(err.response?.data?.error || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  const getSuccessMessage = () => {
    if (accountType === 'portal') {
      return 'Password set successfully! Your registration is now pending admin approval. You will receive an email when your account is approved.';
    } else if (accountType === 'bridge-club') {
      return 'Welcome to Bridge Club! Redirecting to your dashboard...';
    } else {
      return 'Password set successfully! You can now log in.';
    }
  };

  if (loading) {
    return (
      <div className="verify-email-container">
        <div className="verify-email-card">
          <div className="verify-email-loading">
            <div className="spinner"></div>
            <p>Verifying your email...</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="verify-email-container">
        <div className="verify-email-card">
          <div className="verify-email-success">
            <div className="success-icon">✓</div>
            <h2>Success!</h2>
            <p>{getSuccessMessage()}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !tokenInfo?.email_verified) {
    return (
      <div className="verify-email-container">
        <div className="verify-email-card">
          <div className="verify-email-error">
            <div className="error-icon">!</div>
            <h2>Verification Failed</h2>
            <p>{error}</p>
            <button
              className="verify-email-button"
              onClick={() => navigate(paths.redirect)}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show password form
  return (
    <div className="verify-email-container">
      <div className="verify-email-card">
        <div className="verify-email-header">
          <img src="/rbf-logo.png" alt="Red Bridge Farm" className="verify-email-logo" />
          <h1>Set Your Password</h1>
          <p className="verify-email-subtitle">{paths.portalName}</p>
        </div>

        {tokenInfo?.email_verified && (
          <div className="verify-email-verified">
            <span className="check-icon">✓</span>
            Email verified: {tokenInfo.email}
          </div>
        )}

        <form onSubmit={handleSetPassword} className="verify-email-form">
          {error && <div className="verify-email-error-message">{error}</div>}

          <div className="verify-email-field">
            <label>Password</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              minLength={8}
              required
              autoFocus
            />
            <span className="field-hint">At least 8 characters</span>
          </div>

          <div className="verify-email-field">
            <label>Confirm Password</label>
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
            />
          </div>

          <button
            type="submit"
            className="verify-email-button"
            disabled={submitting}
          >
            {submitting ? 'Setting Password...' : 'Set Password & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default VerifyEmail;
